import { useOptionalPlayer } from "../../player/context.js";
import { useDestroy } from "../../utils/use-destroy.js";
import { renderElement } from "../../utils/use-render.js";
import { ThumbnailProvider } from "./context.js";
import { ThumbnailCore, ThumbnailDataAttrs, mapCuesToThumbnails } from "@videojs/core";
import { createThumbnail, selectTextTrack } from "@videojs/core/dom";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/ui/thumbnail/root.tsx
/**
* Resolves, sizes, and clips a thumbnail for a point in time.
*
* Renders a `div` and exposes `data-hidden`, `data-loading`, and `data-error` for styling every layer in the preview.
* Render `Thumbnail.Image` inside it for the image the root controls and measures.
*/
const ThumbnailRoot = forwardRef(function ThumbnailRoot(componentProps, forwardedRef) {
	const { render, className, style, time = 0, thumbnails: externalThumbnails, ...elementProps } = componentProps;
	const [core] = useState(() => new ThumbnailCore());
	const divRef = useRef(null);
	const imgRef = useRef(null);
	const textTrack = useOptionalPlayer(selectTextTrack);
	const [, setRenderToken] = useState(0);
	const [handle] = useState(() => createThumbnail({
		getContainer: () => divRef.current,
		getImg: () => imgRef.current,
		onStateChange: () => setRenderToken((token) => token + 1)
	}));
	useDestroy(handle, () => handle.connect());
	const imageRef = useCallback((img) => {
		const previous = imgRef.current;
		imgRef.current = img;
		if (img) handle.connect();
		else if (previous) handle.disconnectImg(previous);
	}, [handle]);
	const thumbnails = useMemo(() => {
		if (externalThumbnails && externalThumbnails.length > 0) return externalThumbnails;
		return textTrack && textTrack.thumbnailCues.length > 0 ? mapCuesToThumbnails(textTrack.thumbnailCues, textTrack.thumbnailTrackSrc ?? void 0) : [];
	}, [externalThumbnails, textTrack]);
	const thumbnail = useMemo(() => core.findActiveThumbnail(thumbnails, time), [
		core,
		thumbnails,
		time
	]);
	handle.updateSrc(thumbnail?.url);
	const state = core.getState(handle.loading, handle.error, thumbnail);
	let containerStyle = { overflow: "hidden" };
	let imageStyle;
	if (thumbnail && handle.naturalWidth && handle.naturalHeight) {
		const constraints = handle.readConstraints();
		const result = core.resize(thumbnail, handle.naturalWidth, handle.naturalHeight, constraints);
		if (result) {
			containerStyle = {
				overflow: "hidden",
				width: result.containerWidth,
				height: result.containerHeight
			};
			imageStyle = {
				width: result.imageWidth,
				height: result.imageHeight,
				maxWidth: "none",
				transform: result.offsetX || result.offsetY ? `translate(-${result.offsetX}px, -${result.offsetY}px)` : void 0
			};
		}
	}
	return /* @__PURE__ */ jsx(ThumbnailProvider, {
		value: {
			core,
			state,
			src: thumbnail?.url,
			imageStyle,
			inheritedCrossOrigin: externalThumbnails?.length ? void 0 : textTrack?.thumbnailTrackCrossOrigin,
			imageRef
		},
		children: renderElement("div", {
			render,
			className,
			style
		}, {
			state,
			stateAttrMap: ThumbnailDataAttrs,
			ref: [forwardedRef, divRef],
			props: [
				core.getAttrs(state),
				{ style: containerStyle },
				elementProps
			]
		})
	});
});
//#endregion
export { ThumbnailRoot };

//# sourceMappingURL=root.js.map